import React, { useState } from 'react';
import Formatters from 'formatters/_Formatter';
import RecordDetailsLink from 'formatters/RecordDetailsLink';
import { COMBINED } from 'data/dataSourceConstants';
import Highlight from 'components/Highlight';
import SortableHeader from 'components/dataTable/SortableHeader';
import FileUploadButton from 'components/dataTable/FileUploadButton';
import JobApplicationCloseWarning from 'formatters/JobApplicationCloseWarning';

import './DobNowJobApplicationsGroupedTable.css';

function renderCell(col, row, context, options = {}) {
	const { propertyData, dataSet, dataSource, searchString } = context;
	const { showCloseWarning = false } = options;
	let rawContent = row[col.key] || row[col.altKey];

	if (col.key === 'filing_designation') {
		rawContent = row.filing_designation;
	}

	if (col.key === 'filing_status') {
		rawContent = row.displayStatus || row.filing_status;
	}

	let cellContent = rawContent || '-';

	if (col.formatter) {
		const Formatter = Formatters[col.formatter];
		if (Formatter) {
			if (col.formatter === 'ResolutionRequest') {
				cellContent = (
					<Formatter
						text={rawContent}
						rowData={row}
						propertyData={propertyData}
						dataSet={dataSet}
						dataSource={dataSource}
					/>
				);
			} else if (col.formatter === 'StatusBadge' && rawContent) {
				cellContent = (
					<span className="job-application-status-cell">
						<Formatter
							text={rawContent}
							rowData={{ ...row, isOpen: row.filingIsOpen ? 1 : 0 }}
							propertyData={propertyData}
							dataSet={dataSet}
							dataSource={dataSource}
						/>
						{showCloseWarning && <JobApplicationCloseWarning />}
					</span>
				);
			} else if (col.formatter === 'DateFormatter' && rawContent) {
				cellContent = (
					<Formatter
						text={rawContent}
						rowData={row}
						propertyData={propertyData}
						dataSet={dataSet}
						dataSource={dataSource}
					/>
				);
			} else if (rawContent) {
				cellContent = (
					<Formatter
						text={rawContent}
						rowData={row}
						propertyData={propertyData}
						dataSet={dataSet}
						dataSource={dataSource}
					/>
				);
			}
		}
	}

	return (
		<td>
			{typeof cellContent === 'string' ? (
				<Highlight text={cellContent} substring={searchString} />
			) : (
				cellContent
			)}
		</td>
	);
}

function DobNowJobApplicationsGroupedTable(props) {
	const {
		columns,
		groups,
		dataSet,
		dataSource,
		propertyData,
		isLoading,
		searchString,
		sortField,
		sortDirection,
		onSort,
	} = props;

	const [expandedGroups, setExpandedGroups] = useState({});

	const toggleGroup = (jobNumber) => {
		setExpandedGroups((prev) => ({
			...prev,
			[jobNumber]: !prev[jobNumber],
		}));
	};

	const isGroupExpanded = (jobNumber) => expandedGroups[jobNumber] === true;

	let noDataRow = (
		<tr>
			<td colSpan={columns.length + 1} className="nothing-found-row">
				No Data Available
			</td>
		</tr>
	);

	if (isLoading) {
		noDataRow = (
			<tr>
				<td colSpan={columns.length + 1} className="nothing-found-row">
					Loading...
				</td>
			</tr>
		);
	}

	const cellContext = { propertyData, dataSet, dataSource, searchString };
	const isDrawingsCol = (col) => col.formatter === 'ResolutionRequest';
	const isJobNumberCol = (col) => col.key === 'job_number';
	const isBusinessNameCol = (col) => col.key === 'owner_s_business_name';
	const isFilingDesignationCol = (col) => col.key === 'filing_designation';
	const isStatusCol = (col) => col.key === 'filing_status';

	const renderJobNumberCell = (filing, group) => (
		<Highlight
			text={String(filing.job_number || group.jobNumber)}
			substring={searchString}
		/>
	);

	const renderBusinessNameCell = (filing, group) => {
		const businessName =
			filing.owner_s_business_name && filing.owner_s_business_name !== '-'
				? filing.owner_s_business_name
				: group.businessName;
		return (
			<Highlight text={String(businessName || '-')} substring={searchString} />
		);
	};

	const renderFilingRow = (filing, group, options = {}) => {
		const { isSubRow = false, showCloseWarning = false } = options;
		const rowClass = [
			'job-application-doc-row',
			isSubRow ? 'job-application-sub-row' : '',
			filing.filingIsOpen ? 'job-application-open-row' : '',
		]
			.filter(Boolean)
			.join(' ');

		return (
			<tr key={filing.id || `${group.jobNumber}-${filing.filing_designation}`} className={rowClass}>
				<td className="data-table-buttons-cell">
					<div className="data-table-buttons-wrapper">
						<RecordDetailsLink
							isBis={false}
							dataSource={dataSource}
							row={filing}
							dataSet={dataSet}
						/>
						{dataSource !== COMBINED && (
							<FileUploadButton dataSet={dataSet} dataSource={dataSource} row={filing} />
						)}
					</div>
				</td>
				{columns.map((col, j) => {
					if (isDrawingsCol(col)) {
						return (
							<React.Fragment key={`${filing.id || j}-drawings`}>
								{renderCell(col, filing, cellContext)}
							</React.Fragment>
						);
					}

					if (isJobNumberCol(col)) {
						return (
							<td key={`${filing.id || j}-job`}>{renderJobNumberCell(filing, group)}</td>
						);
					}

					if (isBusinessNameCol(col)) {
						return (
							<td key={`${filing.id || j}-business`}>
								{renderBusinessNameCell(filing, group)}
							</td>
						);
					}

					return (
						<React.Fragment key={`${filing.id || j}-${col.key}`}>
							{renderCell(col, filing, cellContext, {
								showCloseWarning: isStatusCol(col) && showCloseWarning,
							})}
						</React.Fragment>
					);
				})}
			</tr>
		);
	};

	const renderGroupToggle = (group, expanded) => (
		<button
			type="button"
			className="btn btn-hyperlink job-group-toggle"
			onClick={() => toggleGroup(group.jobNumber)}
			aria-label={expanded ? 'Collapse group' : 'Expand group'}
		>
			<i className={`bi bi-chevron-${expanded ? 'down' : 'right'}`} />
		</button>
	);

	const renderGroupHeaderCell = (col, group, firstFiling, expanded) => {
		if (isDrawingsCol(col)) {
			return (
				<React.Fragment key={`${group.jobNumber}-header-drawings`}>
					{renderCell(col, firstFiling, cellContext)}
				</React.Fragment>
			);
		}

		if (isJobNumberCol(col)) {
			return (
				<td key={`${group.jobNumber}-header-job`} className="job-group-number">
					<Highlight text={String(group.jobNumber)} substring={searchString} />
				</td>
			);
		}

		if (isBusinessNameCol(col)) {
			return (
				<td key={`${group.jobNumber}-header-business`}>
					<Highlight text={String(group.businessName || '-')} substring={searchString} />
				</td>
			);
		}

		if (isFilingDesignationCol(col)) {
			return (
				<td key={`${group.jobNumber}-header-filing`}>
					<div className="job-group-filing-cell">
						<span className="job-group-filing-designation">
							<Highlight
								text={String(firstFiling.filing_designation || '-')}
								substring={searchString}
							/>
						</span>
						{renderGroupToggle(group, expanded)}
					</div>
				</td>
			);
		}

		if (isStatusCol(col)) {
			return (
				<React.Fragment key={`${group.jobNumber}-header-status`}>
					{renderCell(col, firstFiling, cellContext, {
						showCloseWarning: group.needsCloseWarning,
					})}
				</React.Fragment>
			);
		}

		return (
			<React.Fragment key={`${group.jobNumber}-header-${col.key}`}>
				{renderCell(col, firstFiling, cellContext)}
			</React.Fragment>
		);
	};

	return (
		<table className="table table-striped data-table job-applications-grouped-table">
			<thead>
				<tr>
					<th></th>
					{columns.map((col, i) => (
						<SortableHeader
							key={`${col.key}-${col.label}-${i}`}
							column={col}
							sortField={sortField}
							sortDirection={sortDirection}
							onSort={onSort}
						/>
					))}
				</tr>
			</thead>
			<tbody>
				{(groups.length === 0 || isLoading) && noDataRow}
				{!isLoading &&
					groups.map((group) => {
						const expanded = isGroupExpanded(group.jobNumber);
						const hasMultipleFilings = group.filings.length > 1;
						const firstFiling = group.filings[0];
						const subFilings = group.filings.slice(1);

						if (!hasMultipleFilings) {
							return renderFilingRow(group.filings[0], group, {
								showCloseWarning: group.needsCloseWarning,
							});
						}

						return (
							<React.Fragment key={group.jobNumber}>
								<tr className="job-application-group-header">
									<td className="data-table-buttons-cell">
										<div className="data-table-buttons-wrapper">
											<RecordDetailsLink
												isBis={false}
												dataSource={dataSource}
												row={firstFiling}
												dataSet={dataSet}
											/>
											{dataSource !== COMBINED && (
												<FileUploadButton
													dataSet={dataSet}
													dataSource={dataSource}
													row={firstFiling}
												/>
											)}
										</div>
									</td>
									{columns.map((col) =>
										renderGroupHeaderCell(col, group, firstFiling, expanded)
									)}
								</tr>
								{expanded &&
									subFilings.map((filing, filingIndex) =>
										renderFilingRow(filing, group, {
											isSubRow: true,
											showCloseWarning:
												group.needsCloseWarning &&
												filingIndex === subFilings.length - 1,
										})
									)}
							</React.Fragment>
						);
					})}
			</tbody>
		</table>
	);
}

export default DobNowJobApplicationsGroupedTable;
